import { Injectable, inject } from '@angular/core';
import { CountryToken } from '@app/core/env.tokens';
import { Region } from '@models/Location/region.model';
import { TranslateService } from '@services/logic/translate.service';

@Injectable({
  providedIn: 'root',
})
export class StaticDataService {
  private readonly country = inject(CountryToken);

  constructor(private translateService: TranslateService) {}
  // Fallback only — GEO-REGIONS.isActive is the source of truth (see RegionStore).
  private readonly danishRegions: Region[] = [
    {
      name: 'Aarhus',
      regionURL: 'aarhus',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [10.195657, 56.154359] },
    },
    {
      name: 'Odense',
      regionURL: 'odense',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [10.389232, 55.392889] },
    },
    {
      name: 'Storkøbenhavn',
      regionURL: 'storkoebenhavn',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [12.524029, 55.682451] },
    },
    {
      name: 'Nordsjælland',
      regionURL: 'nordsjaelland',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [12.301777, 55.870799] },
    },
    {
      name: 'Bornholm',
      regionURL: 'bornholm',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [14.925935, 55.274639] },
    },
    {
      name: 'Vestsjælland',
      regionURL: 'vestsjaelland',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [11.354849, 55.406263] },
    },
    {
      name: 'Sydsjælland og øer',
      regionURL: 'sydsjaelland-og-oeer',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [11.903353, 55.028908] },
    },
    {
      name: 'Fyn',
      regionURL: 'fyn',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [10.378113, 55.310307] },
    },
    {
      name: 'Sønderjylland',
      regionURL: 'soenderjylland',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [9.125097, 55.359493] },
    },
    {
      name: 'Midt/Vestjylland',
      regionURL: 'midtvestjylland',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [8.914974, 56.290146] },
    },
    {
      name: 'Nordjylland',
      regionURL: 'nordjylland',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [9.680711, 56.983917] },
    },
    {
      name: 'Østjylland',
      regionURL: 'oestjylland',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [9.995557, 56.185081] },
    },
  ];

  // Fallback only — GEO-REGIONS.isActive is the source of truth (see RegionStore).
  private readonly netherlandsRegions: Region[] = [
    {
      name: 'Drenthe',
      regionURL: 'drenthe',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [6.4561, 52.7742] },
    },
    {
      name: 'Friesland',
      regionURL: 'friesland',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [5.9564, 52.9354] },
    },
    {
      name: 'Guelders',
      regionURL: 'guelders',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [5.9884, 52.2022] },
    },
    {
      name: 'Groningen',
      regionURL: 'groningen',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [6.552, 53.2061] },
    },
    {
      name: 'Limburg',
      regionURL: 'limburg',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [5.6882, 50.8503] },
    },
    {
      name: 'South Holland',
      regionURL: 'south-holland',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [4.6685, 51.8131] },
    },
    {
      name: 'Overijssel',
      regionURL: 'overijssel',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [6.6148, 52.5249] },
    },
    {
      name: 'Flevoland',
      regionURL: 'flevoland',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [5.1885, 52.3735] },
    },
    {
      name: 'North Holland',
      regionURL: 'north-holland',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [4.8038, 52.4606] },
    },
    {
      name: 'Utrecht',
      regionURL: 'utrecht',
      isActive: true,
      coordinates: { type: 'Point', coordinates: [5.176, 52.1555] },
    },
    {
      name: 'Zeeland',
      regionURL: 'zeeland',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [3.6241, 51.5065] },
    },
    {
      name: 'North Brabant',
      regionURL: 'north-brabant',
      isActive: false,
      coordinates: { type: 'Point', coordinates: [4.7396, 51.5751] },
    },
  ];

  /**
   * Returns the regions based on the current environment language.
   * - 'nl' returns Netherlands regions
   * - 'dk' or 'en' returns Danish regions
   */
  get regions(): Region[] {
    const raw =
      this.country === 'nl' ? this.netherlandsRegions : this.danishRegions;
    const labels = this.regionLabels();
    if (!labels) {
      return raw;
    }
    return raw.map((r) => ({
      ...r,
      name: labels[r.regionURL] ?? r.name,
    }));
  }

  /** The hardcoded fallback list for the current country, with i18n labels merged. Used by RegionStore as the initial + fetch-failure value. */
  get fallbackRegions(): Region[] {
    return this.regions; // existing getter already merges labels
  }

  /** Flat map of regionURL -> localized label, sourced from i18n. */
  regionLabels(): Record<string, string> | undefined {
    return this.translateService.data?.STATIC_DATA?.REGIONS;
  }
}
